import { SelectField } from '@measured/puck';
import { PageModel, PanelModel } from '@psyomics/surveyjs-core';

import { Survey, SurveySet } from '../../survey-types';

import * as answerUtils from './answers';
import * as expressionUtils from './expressions';
import * as surveyUtils from './surveys';

const JSON_PARSE_INDENTATION = 2;
const EMPTY_OPTION = { label: 'Please select...', value: '' };

const createQuestionField = (survey: Survey, page?: PageModel, panel?: PanelModel): SelectField => {
  const questionOptions = surveyUtils.getAllQuestions(survey, page, panel).map((question) => ({
    label: question.title,
    value: question.name,
  }));

  return {
    type: 'select',
    label: 'Select Question',
    options: [EMPTY_OPTION, ...questionOptions],
  };
};

const createExpressionField = (
  survey: Survey,
  type: expressionUtils.ExpressionTypes | undefined,
  page?: PageModel
): SelectField => {
  let expressions = expressionUtils.getExpressionQuestions(survey, type);

  if (page) {
    expressions = expressions.filter((q) => page?.questions?.map((pageQuestion) => pageQuestion.name).includes(q.name));
  }
  const expressionOptions = expressions.map((expression) => ({
    label: expression.measureName ?? expression.name,
    value: expression.name,
  }));

  return {
    type: 'select',
    label: 'Select Expression',
    options: [EMPTY_OPTION, ...expressionOptions],
  };
};

const createPageSelectField = (survey: Survey): SelectField => {
  const options = survey.model.pages.map((page) => ({
    label: page.title,
    value: page.name,
  }));

  return {
    options: [EMPTY_OPTION, ...options],
    type: 'select',
    label: 'Select Page from Survey',
  };
};

const createPanelSelectField = (survey: Survey, pageName?: string): SelectField => {
  if (!pageName) {
    return {
      options: [EMPTY_OPTION],
      type: 'select',
      label: 'Select Panel from Survey',
    };
  }
  const page = surveyUtils.getPageByName(survey, pageName);
  const options = page.getPanels().map((panel) => ({
    label: panel.name,
    value: panel.name,
  }));

  return {
    options: [EMPTY_OPTION, ...options],
    type: 'select',
    label: 'Select Panel from Survey',
  };
};

const createSurveySelectField = (surveySet: SurveySet): SelectField => {
  const options = surveySet.map((survey) => ({
    label: survey.respondentType,
    value: survey.respondentType,
  }));

  return {
    options: [EMPTY_OPTION, ...options],
    label: 'Select Respondent',
    type: 'select',
  };
};

const createExampleAnswerJsonField = (survey: Survey, pageName?: string) => {
  const questions = pageName
    ? survey.model.getPageByName(pageName)?.getQuestions(true)
    : survey.model.getAllQuestions();

  const answers = answerUtils.generateAnswersForQuestions(questions);

  return {
    label: 'Example answer JSON',
    placeholder: JSON.stringify(answers, null, JSON_PARSE_INDENTATION),
    type: 'textarea',
  } as const;
};

const createQuestionArraySelectField = (
  { survey, page, panel }: { survey: Survey; page?: PageModel; panel?: PanelModel },
  label = 'Select Questions'
) => {
  const questions = surveyUtils.getAllQuestions(survey, page, panel);

  return {
    arrayFields: {
      question: createQuestionField(survey, page, panel),
    },
    defaultItemProps: {
      question: '',
    },
    getItemSummary: (item: { question: string }) => {
      const questionObj = questions.find((q) => q.name === item.question);
      return questionObj?.title || item.question || 'Select a question';
    },
    label,
    type: 'array',
  } as const;
};

export {
  createExampleAnswerJsonField,
  createExpressionField,
  createPageSelectField,
  createPanelSelectField,
  createQuestionArraySelectField,
  createQuestionField,
  createSurveySelectField,
  EMPTY_OPTION,
};
